import {
  SortDirection,
  SortTypesReceived,
  SortTypesSent,
  TransferGroup,
} from '@wetransfer/transfer/component/Transfers/types';
import Router from '@wetransfer/transfer/modules/router';

export interface TransferSearchParams {
  transferGroup: TransferGroup;
  sortBy: SortTypesReceived | SortTypesSent;
  sortOrder?: SortDirection;
  search?: string;
  id?: string;
  p?: string;
  receivingEmail: string;
  afterDeletion?: string;
}

export default {
  getParamsFromUrl,
  queryToParamsObj,
  resolveSortBy,
  resolveSortOrder,
  resolveTransferGroup,
  setParamsAndNavigate,
  isSortTypesSent,
  isSortTypesReceived,
};

function getParamsFromUrl(): TransferSearchParams {
  // Start with window.location.href
  // split off the query string
  // turn that into params object
  const url = new URL(window.location.href);
  const query = url.search;
  const newParams = queryToParamsObj(query);

  return newParams;
}

function queryToParamsObj(queryStr: string): TransferSearchParams {
  // Turn a query string into URLSearchParams
  // and extract available params into an object
  const searchParams = new URLSearchParams(queryStr);
  const transferGroup = resolveTransferGroup(searchParams); // we need this early to resolve sortBy

  return {
    transferGroup,
    sortBy: resolveSortBy(searchParams, transferGroup),
    sortOrder: resolveSortOrder(searchParams),
    p: resolvePageNumber(searchParams),
    ...(searchParams.has('search') && {
      search: searchParams.get('search'),
    }),
    ...(searchParams.has('id') && { id: searchParams.get('id') }),
    ...(searchParams.has('receivingEmail') && {
      receivingEmail: searchParams.get('receivingEmail'),
    }),
    ...(searchParams.has('afterDeletion') && {
      afterDeletion: searchParams.get('afterDeletion'),
    }),
  } as TransferSearchParams;
}

function resolveSortBy(
  searchParams: URLSearchParams,
  transferGroup: TransferGroup
): SortTypesReceived | SortTypesSent {
  let sortBy: string;

  if (searchParams.has('sortBy')) {
    sortBy = searchParams.get('sortBy');

    if (
      (transferGroup === 'sent' && isSortTypesSent(sortBy)) ||
      (transferGroup === 'received' && isSortTypesReceived(sortBy))
    ) {
      return sortBy as SortTypesReceived | SortTypesSent;
    }
  }

  return 'DATE';
}

function resolveSortOrder(searchParams: URLSearchParams): SortDirection {
  const sortOrder = searchParams.get('sortOrder');

  if (
    searchParams.has('sortOrder') &&
    (sortOrder === 'ASC' || sortOrder === 'DESC')
  ) {
    return sortOrder;
  }

  return 'DESC';
}

function resolveTransferGroup(searchParams: URLSearchParams): TransferGroup {
  // In the URL, we shorten transferGroup to 'group'
  const transferGroup = searchParams.get('group');

  if (
    searchParams.has('group') &&
    // It can only be one of two groups
    ['sent', 'received'].includes(transferGroup)
  ) {
    return transferGroup as TransferGroup;
  }

  return 'sent';
}

function resolvePageNumber(searchParams: URLSearchParams): string {
  if (searchParams.has('p')) {
    const pageNumber = searchParams.get('p');

    if (!isNaN(Number(pageNumber)) && Number(pageNumber) > 0) {
      return pageNumber;
    }
  }

  return '1';
}

export function setParamsAndNavigate(
  params: Partial<TransferSearchParams>,
  replaceHistory: boolean
): void {
  const newParams = {
    // Overwrite current route params with incoming params
    ...getParamsFromUrl(),
    ...params,
  };

  const {
    transferGroup,
    sortOrder,
    sortBy,
    search,
    id,
    p,
    receivingEmail,
    afterDeletion,
  } = newParams;

  const searchParams = new URLSearchParams();

  transferGroup !== undefined && searchParams.set('group', transferGroup);
  sortBy !== undefined && searchParams.set('sortBy', sortBy);
  sortOrder !== undefined && searchParams.set('sortOrder', sortOrder);
  search !== undefined && searchParams.set('search', search);
  id !== undefined && searchParams.set('id', id);
  p !== undefined && searchParams.set('p', p);
  receivingEmail !== undefined &&
    searchParams.set('receivingEmail', receivingEmail);
  afterDeletion !== undefined &&
    searchParams.set('afterDeletion', afterDeletion);

  Router.navigateTo('/transfers', replaceHistory, {
    search: searchParams.toString(),
  });
}

function isSortTypesSent(sortByKey: string): sortByKey is SortTypesSent {
  return ['DATE', 'TITLE', 'EXPIRATION', 'SIZE'].includes(sortByKey);
}

function isSortTypesReceived(
  sortByKey: string
): sortByKey is SortTypesReceived {
  return ['DATE', 'SENDER', 'EXPIRATION', 'SIZE'].includes(sortByKey);
}
